/**
 * ChannelingDetailBlock.js
 *
 * Componente de página de detalle para el editor visual CraftJS.
 * Renderiza el artículo completo de una canalización:
 *   - Imagen destacada (opcional)
 *   - Título con línea decorativa
 *   - Metadata (canalizado por / canalizador)
 *   - Cuerpo del texto
 *   - Bloque de cita resaltada (opcional)
 *   - Firma (opcional)
 *   - Footer de créditos: música, video, adaptado por (opcional)
 *   - Botones de compartir (opcional)
 *   - Botones flotantes: reproducir música / leer en voz alta (opcional)
 *
 * Presets disponibles (usar en Toolbox):
 *   DETAIL_PRESETS.dark   → tema oscuro, acento naranja
 *   DETAIL_PRESETS.light  → tema claro, acento azul
 */
import React, { useState, useRef } from 'react';
import { useNode, useEditor } from '@craftjs/core';
import {
  TextField,
  FormControl,
  FormLabel,
  Typography,
  Divider,
  Switch,
  Select,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// ─────────────────────────────────────────────────────────────────────────────
// Estilos del Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  settings: {
    padding: theme.spacing(2),
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.08em',
    color: '#888',
    textTransform: 'uppercase',
    margin: theme.spacing(1.5, 0, 0.5),
  },
  previewBox: {
    width: '100%',
    height: 32,
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    marginBottom: theme.spacing(1),
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Presets
// ─────────────────────────────────────────────────────────────────────────────
export const DETAIL_PRESETS = {
  dark: {
    // Colores generales
    bgColor:        '#424a4a',
    accentColor:    '#ff5722',
    textColor:      'rgba(255,255,255,0.9)',
    subtleColor:    'rgba(255,255,255,0.6)',
    cardBg:         'rgba(255,255,255,0.02)',
    borderColor:    'rgba(255,255,255,0.1)',
    // Imagen
    showImage:      false,
    imageUrl:       '',
    // Título
    title:          'Mensaje de Luz y Esperanza',
    // Meta
    channeledLabel: 'Canalizado por:',
    channeledBy:    'Jeshua',
    channelerLabel: 'Canalizador:',
    channelerName:  'Nombre del Canalizador',
    // Cuerpo
    bodyText:       'Queridos hermanos y hermanas de luz,\n\nEs con gran amor y alegría que me dirijo a ustedes en este momento sagrado. Soy Jeshua, y vengo a compartir con ustedes un mensaje de esperanza y transformación para estos tiempos de cambio.\n\nEl amor es la fuerza más poderosa del universo. Cuando eligen el amor en lugar del miedo, cuando eligen la compasión en lugar del juicio, están alineándose con la frecuencia más elevada de la creación.',
    // Cita
    showQuote:      true,
    quoteText:      'Recuerden que ustedes son co-creadores de su realidad. Cada pensamiento, cada emoción, cada acción emite una vibración que atrae experiencias resonantes con esa frecuencia.',
    // Firma
    showSignature:  true,
    signatureText:  'Con amor infinito,\nJeshua',
    // Créditos footer
    showFooterMeta: true,
    musicLabel:     'Interpretada por Juan Sánchez',
    musicUrl:       '',
    videoLabel:     'Mensaje original en YouTube',
    videoUrl:       '',
    adaptedBy:      'Jeshua®',
    // Compartir
    showShare:      true,
    shareTitle:     'Comparte este mensaje:',
    // Botones flotantes
    showFloating:   true,
    musicFileUrl:   '',
    // Ancho máximo
    maxWidth:       900,
  },
  light: {
    bgColor:        '#f4f4f4',
    accentColor:    '#3f51b5',
    textColor:      '#212121',
    subtleColor:    '#757575',
    cardBg:         '#ffffff',
    borderColor:    '#e0e0e0',
    showImage:      false,
    imageUrl:       '',
    title:          'Mensaje de Luz y Esperanza',
    channeledLabel: 'Canalizado por:',
    channeledBy:    'Jeshua',
    channelerLabel: 'Canalizador:',
    channelerName:  'Nombre del Canalizador',
    bodyText:       'Queridos hermanos y hermanas de luz,\n\nEs con gran amor y alegría que me dirijo a ustedes en este momento sagrado. Soy Jeshua, y vengo a compartir con ustedes un mensaje de esperanza y transformación para estos tiempos de cambio.\n\nEl amor es la fuerza más poderosa del universo. Cuando eligen el amor en lugar del miedo, están alineándose con la frecuencia más elevada de la creación.',
    showQuote:      true,
    quoteText:      'Recuerden que ustedes son co-creadores de su realidad. Cada pensamiento, cada emoción, cada acción emite una vibración que atrae experiencias resonantes con esa frecuencia.',
    showSignature:  true,
    signatureText:  'Con amor infinito,\nJeshua',
    showFooterMeta: true,
    musicLabel:     'Interpretada por Juan Sánchez',
    musicUrl:       '',
    videoLabel:     'Mensaje original en YouTube',
    videoUrl:       '',
    adaptedBy:      'Jeshua®',
    showShare:      true,
    shareTitle:     'Comparte este mensaje:',
    showFloating:   true,
    musicFileUrl:   '',
    maxWidth:       900,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export const ChannelingDetailBlock = ({
  // Colores
  bgColor,
  accentColor,
  textColor,
  subtleColor,
  cardBg,
  borderColor,
  // Imagen
  showImage,
  imageUrl,
  // Título
  title,
  // Meta
  channeledLabel,
  channeledBy,
  channelerLabel,
  channelerName,
  // Cuerpo
  bodyText,
  // Cita
  showQuote,
  quoteText,
  // Firma
  showSignature,
  signatureText,
  // Créditos
  showFooterMeta,
  musicLabel,
  musicUrl,
  videoLabel,
  videoUrl,
  adaptedBy,
  // Compartir
  showShare,
  shareTitle,
  // Flotantes
  showFloating,
  musicFileUrl,
  // Layout
  maxWidth,
}) => {
  const { connectors: { connect, drag } } = useNode();
  const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));

  // ── Estado local para botones flotantes (solo en modo publicado) ────────────
  const [isPlaying, setIsPlaying]   = useState(false);
  const [isReading, setIsReading]   = useState(false);
  const audioRef                    = useRef(null);

  // ── Manejadores de botones flotantes ───────────────────────────────────────
  const handleMusicToggle = () => {
    if (enabled) return; // no actuar en modo edición
    if (!musicFileUrl) return;

    if (!audioRef.current) {
      audioRef.current       = new Audio(musicFileUrl);
      audioRef.current.loop  = true;
      audioRef.current.volume = 0.3;
    }
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleReadToggle = () => {
    if (enabled) return;
    const synthesis = window.speechSynthesis;
    if (isReading) {
      synthesis.cancel();
      setIsReading(false);
    } else {
      const utterance  = new SpeechSynthesisUtterance(bodyText || '');
      utterance.lang   = 'es-ES';
      utterance.rate   = 0.9;
      utterance.onend  = () => setIsReading(false);
      utterance.onerror = () => setIsReading(false);
      synthesis.speak(utterance);
      setIsReading(true);
    }
  };

  // ── Manejadores de compartir ───────────────────────────────────────────────
  const handleShare = (type) => {
    if (enabled) return;
    const url  = window.location.href;
    const text = encodeURIComponent(title || '');
    const actions = {
      whatsapp: () => window.open(`https://wa.me/?text=${text}%20${encodeURIComponent(url)}`, '_blank'),
      facebook: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank'),
      email:    () => window.open(`mailto:?subject=${text}&body=${encodeURIComponent(url)}`),
      copy:     () => {
        navigator.clipboard.writeText(url).catch(() => {});
        alert('¡Enlace copiado!');
      },
    };
    if (actions[type]) actions[type]();
  };

  // ── Estilos en línea ────────────────────────────────────────────────────────
  const wrapperStyle = {
    backgroundColor: bgColor,
    minHeight: '100vh',
    fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    lineHeight: 1.8,
    color: textColor,
    position: 'relative',
  };

  const containerStyle = {
    maxWidth: maxWidth || 900,
    margin: '0 auto',
    padding: '20px 20px 60px',
  };

  const contentCardStyle = {
    backgroundColor: cardBg,
    borderRadius: 8,
    padding: '40px',
  };

  const imageStyle = {
    width: '100%',
    height: 'auto',
    maxHeight: 600,
    objectFit: 'contain',
    borderRadius: 8,
    marginBottom: 40,
    display: 'block',
  };

  const titleStyle = {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: 24,
    lineHeight: 1.2,
    color: textColor,
    textAlign: 'center',
    position: 'relative',
    paddingBottom: 20,
  };

  const titleUnderlineStyle = {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 100,
    height: 3,
    background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
    borderRadius: 2,
  };

  const metaHeaderStyle = {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    justifyContent: 'flex-end',
    columnGap: 12,
    rowGap: 4,
    paddingBottom: 28,
    marginBottom: 36,
    borderBottom: `2px solid ${borderColor}`,
    marginTop: 28,
  };

  const metaLabelStyle = {
    textAlign: 'right',
    color: textColor,
    fontSize: '0.95rem',
    fontWeight: 400,
  };

  const metaValueAccentStyle = {
    textAlign: 'left',
    color: accentColor,
    fontSize: '0.95rem',
    fontWeight: 700,
  };

  const metaValueStyle = {
    textAlign: 'left',
    color: textColor,
    fontSize: '0.95rem',
    fontWeight: 700,
  };

  const bodyParagraphStyle = {
    fontSize: '1.1rem',
    lineHeight: 1.9,
    color: textColor,
    marginBottom: 24,
  };

  const quoteStyle = {
    borderLeft: `3px solid ${accentColor}`,
    padding: '20px 24px',
    margin: '32px 0',
    fontStyle: 'italic',
    color: textColor,
    fontSize: '1.15rem',
    backgroundColor: 'rgba(128,128,128,0.06)',
    lineHeight: 1.7,
  };

  const signatureStyle = {
    textAlign: 'left',
    fontSize: '1.2rem',
    color: accentColor,
    fontStyle: 'italic',
    marginTop: 40,
    paddingTop: 24,
    paddingBottom: 24,
    borderTop: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    fontFamily: "'Georgia', serif",
    whiteSpace: 'pre-line',
  };

  const footerContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginTop: 24,
    paddingBottom: 24,
    borderBottom: `1px solid ${borderColor}`,
  };

  const footerItemStyle = {
    color: subtleColor,
    fontSize: '0.9rem',
    marginBottom: 4,
    lineHeight: 1.6,
  };

  const footerLinkStyle = {
    color: accentColor,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontWeight: 400,
  };

  const shareSectionStyle = {
    marginTop: 40,
    textAlign: 'center',
  };

  const shareTitleStyle = {
    fontSize: '1rem',
    color: subtleColor,
    marginBottom: 16,
  };

  const shareButtonsStyle = {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    flexWrap: 'wrap',
  };

  const shareButtonStyle = {
    backgroundColor: 'rgba(128,128,128,0.15)',
    border: 'none',
    color: textColor,
    padding: '10px 20px',
    borderRadius: 4,
    cursor: enabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.9rem',
    opacity: enabled ? 0.7 : 1,
  };

  const floatingWrapperStyle = {
    position: 'fixed',
    bottom: 30,
    right: 30,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    zIndex: 1000,
  };

  const floatingBtnStyle = (active) => ({
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: active ? '#555' : accentColor,
    border: 'none',
    color: '#fff',
    cursor: enabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 4px 12px ${accentColor}66`,
    fontSize: '1.4rem',
    opacity: enabled ? 0.6 : 1,
  });

  // ── Parsear bodyText en párrafos ────────────────────────────────────────────
  const paragraphs = (bodyText || '').split('\n').filter((p) => p.trim() !== '');

  return (
    <div ref={(ref) => connect(drag(ref))} style={wrapperStyle}>

      {/* ── Contenido principal ─────────────────────────────────────── */}
      <div style={containerStyle}>
        <div style={contentCardStyle}>

          {/* Imagen destacada */}
          {showImage && imageUrl && (
            <img src={imageUrl} alt={title} style={imageStyle} />
          )}

          {/* Título */}
          <h1 style={titleStyle}>
            {title}
            <span style={titleUnderlineStyle} />
          </h1>

          {/* Metadata */}
          <div style={metaHeaderStyle}>
            <span style={metaLabelStyle}>{channeledLabel}</span>
            <span style={metaValueAccentStyle}>{channeledBy}</span>
            <span style={metaLabelStyle}>{channelerLabel}</span>
            <span style={metaValueStyle}>{channelerName}</span>
          </div>

          {/* Cuerpo del texto */}
          <div>
            {paragraphs.map((para, i) => (
              <p key={i} style={bodyParagraphStyle}>{para}</p>
            ))}
          </div>

          {/* Cita */}
          {showQuote && quoteText && (
            <blockquote style={quoteStyle}>
              {quoteText}
            </blockquote>
          )}

          {/* Firma */}
          {showSignature && signatureText && (
            <div style={signatureStyle}>
              {signatureText}
            </div>
          )}

          {/* Footer de créditos */}
          {showFooterMeta && (
            <div style={footerContainerStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'right' }}>
                {musicLabel && (
                  <div style={footerItemStyle}>
                    <strong style={{ color: textColor, fontWeight: 500, marginRight: 4 }}>Música:</strong>
                    {musicUrl ? (
                      <a
                        href={enabled ? undefined : musicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={footerLinkStyle}
                        onClick={enabled ? (e) => e.preventDefault() : undefined}
                      >
                        {musicLabel}
                        <span className="material-icons" style={{ fontSize: 16, opacity: 0.7 }}>open_in_new</span>
                      </a>
                    ) : (
                      <span style={{ color: subtleColor }}>{musicLabel}</span>
                    )}
                  </div>
                )}
                {videoLabel && (
                  <div style={footerItemStyle}>
                    <strong style={{ color: textColor, fontWeight: 500, marginRight: 4 }}>Video:</strong>
                    {videoUrl ? (
                      <a
                        href={enabled ? undefined : videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={footerLinkStyle}
                        onClick={enabled ? (e) => e.preventDefault() : undefined}
                      >
                        {videoLabel}
                        <span className="material-icons" style={{ fontSize: 16, opacity: 0.7 }}>open_in_new</span>
                      </a>
                    ) : (
                      <span style={{ color: subtleColor }}>{videoLabel}</span>
                    )}
                  </div>
                )}
                {adaptedBy && (
                  <div style={footerItemStyle}>
                    <strong style={{ color: textColor, fontWeight: 500, marginRight: 4 }}>Adaptado por:</strong>
                    <span>{adaptedBy}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Botones de compartir */}
          {showShare && (
            <div style={shareSectionStyle}>
              <p style={shareTitleStyle}>{shareTitle}</p>
              <div style={shareButtonsStyle}>
                {[
                  { type: 'email',    icon: 'email',    label: 'Email'    },
                  { type: 'whatsapp', icon: 'share',    label: 'WhatsApp' },
                  { type: 'facebook', icon: 'facebook', label: 'Facebook' },
                  { type: 'copy',     icon: 'link',     label: 'Copiar enlace' },
                ].map(({ type, icon, label }) => (
                  <button
                    key={type}
                    style={shareButtonStyle}
                    onClick={() => handleShare(type)}
                  >
                    <span className="material-icons" style={{ fontSize: '1.1rem' }}>{icon}</span>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Botones flotantes (solo si showFloating) ────────────────── */}
      {showFloating && (
        <div style={floatingWrapperStyle}>
          {/* Música */}
          <button
            style={floatingBtnStyle(isPlaying)}
            title={isPlaying ? 'Pausar música' : 'Reproducir música de fondo'}
            onClick={handleMusicToggle}
          >
            <span className="material-icons">
              {isPlaying ? 'music_off' : 'music_note'}
            </span>
          </button>
          {/* Leer en voz alta */}
          <button
            style={floatingBtnStyle(isReading)}
            title={isReading ? 'Detener lectura' : 'Leer texto en voz alta'}
            onClick={handleReadToggle}
          >
            <span className="material-icons">
              {isReading ? 'stop' : 'record_voice_over'}
            </span>
          </button>
        </div>
      )}

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const ChannelingDetailBlockSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    bgColor, accentColor, textColor, subtleColor, cardBg, borderColor,
    showImage, imageUrl,
    title,
    channeledLabel, channeledBy, channelerLabel, channelerName,
    bodyText,
    showQuote, quoteText,
    showSignature, signatureText,
    showFooterMeta, musicLabel, musicUrl, videoLabel, videoUrl, adaptedBy,
    showShare, shareTitle,
    showFloating, musicFileUrl,
    maxWidth,
  } = useNode((node) => ({ ...node.data.props }));

  return (
    <div className={classes.settings}>

      {/* ── COLORES ─────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Colores</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Fondo general</FormLabel>
        <div className={classes.previewBox} style={{ backgroundColor: bgColor }} />
        <TextField
          type="color"
          size="small"
          value={bgColor || '#424a4a'}
          onChange={(e) => setProp((p) => (p.bgColor = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color de acento</FormLabel>
        <div className={classes.previewBox} style={{ backgroundColor: accentColor }} />
        <TextField
          type="color"
          size="small"
          value={accentColor || '#ff5722'}
          onChange={(e) => setProp((p) => (p.accentColor = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Fondo tarjeta contenido</FormLabel>
        <TextField
          size="small"
          value={cardBg || 'rgba(255,255,255,0.02)'}
          onChange={(e) => setProp((p) => (p.cardBg = e.target.value))}
          placeholder="rgba(255,255,255,0.02)"
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Ancho máximo (px)</FormLabel>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 400, max: 1400, step: 50 }}
          value={maxWidth || 900}
          onChange={(e) => setProp((p) => (p.maxWidth = parseInt(e.target.value)))}
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── IMAGEN ──────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Imagen destacada</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Mostrar imagen</Typography>
        <Switch
          size="small"
          checked={!!showImage}
          onChange={(e) => setProp((p) => (p.showImage = e.target.checked))}
        />
      </div>

      {showImage && (
        <FormControl fullWidth margin="dense">
          <FormLabel>URL de imagen</FormLabel>
          <TextField
            size="small"
            value={imageUrl || ''}
            onChange={(e) => setProp((p) => (p.imageUrl = e.target.value))}
            placeholder="https://ejemplo.com/imagen.jpg"
          />
        </FormControl>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* ── TÍTULO ──────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Título</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Título</FormLabel>
        <TextField
          size="small"
          value={title || ''}
          onChange={(e) => setProp((p) => (p.title = e.target.value))}
          placeholder="Título de la canalización"
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── METADATA ────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Metadata</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Etiqueta "canalizado por"</FormLabel>
        <TextField
          size="small"
          value={channeledLabel || ''}
          onChange={(e) => setProp((p) => (p.channeledLabel = e.target.value))}
          placeholder="Canalizado por:"
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Nombre canalizado</FormLabel>
        <TextField
          size="small"
          value={channeledBy || ''}
          onChange={(e) => setProp((p) => (p.channeledBy = e.target.value))}
          placeholder="Jeshua"
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Etiqueta "canalizador"</FormLabel>
        <TextField
          size="small"
          value={channelerLabel || ''}
          onChange={(e) => setProp((p) => (p.channelerLabel = e.target.value))}
          placeholder="Canalizador:"
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Nombre canalizador</FormLabel>
        <TextField
          size="small"
          value={channelerName || ''}
          onChange={(e) => setProp((p) => (p.channelerName = e.target.value))}
          placeholder="Nombre del Canalizador"
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── CUERPO ──────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Cuerpo del texto</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Texto (párrafos separados por línea en blanco)</FormLabel>
        <TextField
          size="small"
          multiline
          minRows={6}
          maxRows={16}
          value={bodyText || ''}
          onChange={(e) => setProp((p) => (p.bodyText = e.target.value))}
          placeholder="Escribe el contenido de la canalización..."
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── CITA ────────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Cita resaltada</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Mostrar cita</Typography>
        <Switch
          size="small"
          checked={!!showQuote}
          onChange={(e) => setProp((p) => (p.showQuote = e.target.checked))}
        />
      </div>

      {showQuote && (
        <FormControl fullWidth margin="dense">
          <FormLabel>Texto de la cita</FormLabel>
          <TextField
            size="small"
            multiline
            minRows={3}
            value={quoteText || ''}
            onChange={(e) => setProp((p) => (p.quoteText = e.target.value))}
            placeholder="Texto de la cita destacada..."
          />
        </FormControl>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* ── FIRMA ───────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Firma</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Mostrar firma</Typography>
        <Switch
          size="small"
          checked={!!showSignature}
          onChange={(e) => setProp((p) => (p.showSignature = e.target.checked))}
        />
      </div>

      {showSignature && (
        <FormControl fullWidth margin="dense">
          <FormLabel>Texto firma</FormLabel>
          <TextField
            size="small"
            multiline
            minRows={2}
            value={signatureText || ''}
            onChange={(e) => setProp((p) => (p.signatureText = e.target.value))}
            placeholder="Con amor infinito,&#10;Jeshua"
          />
        </FormControl>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* ── CRÉDITOS ────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Créditos / Footer</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Mostrar créditos</Typography>
        <Switch
          size="small"
          checked={!!showFooterMeta}
          onChange={(e) => setProp((p) => (p.showFooterMeta = e.target.checked))}
        />
      </div>

      {showFooterMeta && (
        <>
          <FormControl fullWidth margin="dense">
            <FormLabel>Etiqueta música</FormLabel>
            <TextField
              size="small"
              value={musicLabel || ''}
              onChange={(e) => setProp((p) => (p.musicLabel = e.target.value))}
              placeholder="Interpretada por..."
            />
          </FormControl>
          <FormControl fullWidth margin="dense">
            <FormLabel>URL Spotify / música</FormLabel>
            <TextField
              size="small"
              value={musicUrl || ''}
              onChange={(e) => setProp((p) => (p.musicUrl = e.target.value))}
              placeholder="https://open.spotify.com/..."
            />
          </FormControl>
          <FormControl fullWidth margin="dense">
            <FormLabel>Etiqueta video</FormLabel>
            <TextField
              size="small"
              value={videoLabel || ''}
              onChange={(e) => setProp((p) => (p.videoLabel = e.target.value))}
              placeholder="Ver en YouTube..."
            />
          </FormControl>
          <FormControl fullWidth margin="dense">
            <FormLabel>URL YouTube / video</FormLabel>
            <TextField
              size="small"
              value={videoUrl || ''}
              onChange={(e) => setProp((p) => (p.videoUrl = e.target.value))}
              placeholder="https://www.youtube.com/..."
            />
          </FormControl>
          <FormControl fullWidth margin="dense">
            <FormLabel>Adaptado por</FormLabel>
            <TextField
              size="small"
              value={adaptedBy || ''}
              onChange={(e) => setProp((p) => (p.adaptedBy = e.target.value))}
              placeholder="Jeshua®"
            />
          </FormControl>
        </>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* ── COMPARTIR ───────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Compartir</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Mostrar botones compartir</Typography>
        <Switch
          size="small"
          checked={!!showShare}
          onChange={(e) => setProp((p) => (p.showShare = e.target.checked))}
        />
      </div>

      {showShare && (
        <FormControl fullWidth margin="dense">
          <FormLabel>Título sección compartir</FormLabel>
          <TextField
            size="small"
            value={shareTitle || ''}
            onChange={(e) => setProp((p) => (p.shareTitle = e.target.value))}
            placeholder="Comparte este mensaje:"
          />
        </FormControl>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* ── BOTONES FLOTANTES ───────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Botones flotantes</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Mostrar música / leer</Typography>
        <Switch
          size="small"
          checked={!!showFloating}
          onChange={(e) => setProp((p) => (p.showFloating = e.target.checked))}
        />
      </div>

      {showFloating && (
        <FormControl fullWidth margin="dense">
          <FormLabel>URL archivo MP3 de fondo</FormLabel>
          <TextField
            size="small"
            value={musicFileUrl || ''}
            onChange={(e) => setProp((p) => (p.musicFileUrl = e.target.value))}
            placeholder="https://ejemplo.com/musica.mp3"
          />
        </FormControl>
      )}

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// craft config — usa preset dark como defaults
// ─────────────────────────────────────────────────────────────────────────────
ChannelingDetailBlock.craft = {
  displayName: 'Detalle Canalización',
  props: { ...DETAIL_PRESETS.dark },
  related: {
    settings: ChannelingDetailBlockSettings,
  },
};
